import React, { useEffect, useRef, useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';
// import store from '@store';
// import { Provider } from 'react-redux';
import { Routes } from '@router';
import Boot from './boot';
import { api } from '@api';
import { lightTheme, GlobalStyles } from '@theme';
import NotFoundPage from "@pages/404/NotFound.js";
import "../i18n";
import { messaging, getToken, VAPID_KEY, onMessage } from "../firebase";
import { Modal } from "antd";
import { useTranslation } from 'react-i18next';
import { useSelector } from "react-redux";
import { useAtom } from 'jotai';
import { orderCount } from '../utils/atom';
import { CloseOutlined, InboxOutlined } from '@ant-design/icons';


const StyledAlertModal = styled(Modal)`
  &.ant-modal {
    position: fixed;
    top: 18px;
    right: 18px;
    margin: 0;
    padding-bottom: 0;
    width: 390px !important;
    max-width: calc(100vw - 24px);
  }

  .ant-modal-content {
    position: relative;
    overflow: hidden;
    border: none;
    border-radius: 18px;
    padding: 0;
    background: linear-gradient(
      135deg,
      #1f67ff 0%,
      #1557f5 55%,
      #0f48db 100%
    );
    box-shadow:
      0 24px 50px rgba(21, 87, 245, 0.35),
      0 10px 24px rgba(0, 0, 0, 0.12);
  }

  .ant-modal-header,
  .ant-modal-footer,
  .ant-modal-close {
    display: none;
  }

  .ant-modal-body {
    padding: 0;
  }

  @media (max-width: 480px) {
    &.ant-modal {
      right: 12px;
      left: 12px;
      width: auto !important;
    }
  }
`;

const AlertBody = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: pointer;
`;

const IconBadge = styled.div`
  flex-shrink: 0;

  width: 46px;
  height: 46px;

  border-radius: 12px;

  background: rgba(255, 255, 255, 0.92);

  display: flex;
  justify-content: center;
  align-items: center;

  font-size: 22px;
  color: #1565ff;

  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
`;

const AlertTitle = styled.div`
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 6px;
  padding-right: 34px;
`;
const AlertText = styled.div`
  color: rgba(255, 255, 255, 0.95);
  font-size: 15px;
  line-height: 1.45;
  font-weight: 400;

  word-break: break-word;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;

  width: 30px;
  height: 30px;

  border: none;
  outline: none;

  border-radius: 50%;

  background: transparent;

  color: rgba(255, 255, 255, 0.9);

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;

  transition: all .2s;

  &:hover {
    background: rgba(255,255,255,.15);
    transform: rotate(90deg);
  }
`;

const CardWrapper = styled.div`
  position: relative;
  padding: 18px 18px 16px;
`;

const CancelButton = styled.button`
  height: 38px;
  padding: 0 18px;

  border-radius: 10px;
  border: 1px solid rgba(255,255,255,.28);

  background: rgba(255,255,255,.12);
  backdrop-filter: blur(8px);

  color: #fff;
  font-size: 14px;
  font-weight: 600;

  cursor: pointer;
  transition: .2s;

  &:hover{
    background: rgba(255,255,255,.2);
    border-color: rgba(255,255,255,.45);
  }
`;

const GoButton = styled.button`
  height: 38px;
  padding: 0 18px;

  border: none;
  border-radius: 10px;

  background: #fff;
  color: #1565ff;

  font-size: 14px;
  font-weight: 700;

  cursor: pointer;
  transition: .2s;

  &:hover{
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(0,0,0,.18);
  }

  &:active{
    transform: translateY(0);
  }
`;



function App() {
  const [firebaseToken, setFirebaseToken] = useState(null);
  const notificationSound = useRef(new Audio("/incomeing.mp3"));
  const [domainNotFound, setDomainNotFound] = useState(false);
  const [subText, setSubText] = useState("");
  // const [businessInfo, setBusinessInfo] = useState();
  const [orderStatus, setOrderStatus] = useAtom(orderCount);

  const [orderAlert, setOrderAlert] = useState(null);
  const [checkInit, setCheckInit] = useState();


  const {t,i18n} = useTranslation();

    const token = useSelector((state) => state.auth.token);

useEffect(() => {
  getCheckSubDomain();
  if (token) {
    initFirebase();
  }
}, [token]);

  useEffect(() => {
    getCheckSubDomain();
    initFirebase();
    const handleSWMessage = (event) => {
      if (event.data?.type === "ORDER_RECEIVED") {
        const payload = event.data.payload;
        const title = payload.notification?.title || "New Order";
        const body = payload.notification?.body || "";
        const orderId = payload.data?.id || "";

        playSound();
        setOrderAlert({ title, body, orderId });
      } else if (event.data?.type === "STOP_SOUND") {
        stopSound();
      }
    };

    navigator.serviceWorker.addEventListener("message", handleSWMessage);

    return () => {
      navigator.serviceWorker.removeEventListener("message", handleSWMessage);
    };
  }, [token]);

  const playSound = () => {
    notificationSound.current.currentTime = 0;
    notificationSound.current.play().catch(() => {});
    setOrderStatus(!orderStatus)
  };

  const stopSound = () => {
    notificationSound.current.pause();
    notificationSound.current.currentTime = 0;
    setOrderStatus(!orderStatus)
  };

  const requestNotificationPermission = async () => {
    if (!("Notification" in window)) return false;
    if (Notification.permission === "granted") return true;
    const permission = await Notification.requestPermission();
    return permission === "granted";
  };

  // 🔹 Firebase initialization
  const initFirebase = async () => {
    try {
      const swRegistration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js"
      );

      const isAllowed = await requestNotificationPermission();
      if (!isAllowed) return;

      await handleFirebaseSubscription(swRegistration);

      // Foreground messages
      onMessage(messaging, (payload) => {
        const title = payload.notification?.title || "New Order";
        const body = payload.notification?.body || "";
        const orderId = payload.data?.id || "";

        playSound();
        setOrderAlert({ title, body, orderId });
        setOrderStatus(!orderStatus)
      });
    } catch (error) {
      console.error("Service Worker Error:", error);
    }
  };

  const handleFirebaseSubscription = async (swRegistration) => {
    if (firebaseToken) return; 

    const token = localStorage.getItem("token");
    const adminData = localStorage.getItem("admin");
    const admin = adminData ? JSON.parse(adminData) : null;
    const allowedRoles = ["MERCHANT_ADMIN", "ORDER_ADMIN"];
    if (!token || !admin || !allowedRoles.includes(admin?.role)) return;

    const currentToken = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swRegistration,
    });
    if (!currentToken) return;

    setFirebaseToken(currentToken);

    await api.firebaseSub({
      token: currentToken,
      store_id: admin?.store_id,
      lang: i18n.language === "mm" ? "my" : i18n.language === "zh" ? i18n.language : "en",
    });

  };

  const getCheckSubDomain = async () => {
    try {
      await api.getCheckSubDomain();
    } catch (error) {
      const message = error;
      if (message === "Domain not found.") {
        setDomainNotFound(true);
        setSubText("Sorry, the url you visited does not exist.");
      } else if (message === "Store not found.") {
        setDomainNotFound(true);
        setSubText("Sorry, the store you visited has been deleted.");
      } else {
        setDomainNotFound(false);
        setSubText("");
      }
      console.log("error", error);
    }
  };
//init app data
const getCheckInit = () => {
    api
      .getCheckSubDomain()
      .then(({ data }) => {
        setCheckInit(data?.business_info?.data);
      })
      .catch((error) => {
       console.log(error)
      });
  };

const updateFavicon = (iconUrl) => {
  const existingIcons = document.querySelectorAll("link[rel*='icon']");
  existingIcons.forEach((el) => el.remove());

  const link = document.createElement("link");
  link.rel = "icon";
  link.type = "image/png";

  // add cache buster
  link.href = `${iconUrl}?v=${Date.now()}`;

  document.head.appendChild(link);
};

const updateManifest = (business) => {
  const oldManifest = document.querySelector("link[rel='manifest']");
  if (oldManifest) oldManifest.remove();
  const name = checkInit?.name?.primary?.trim() ? `${business?.name?.primary} - Business Admin` : "Business Admin";

 const manifest = {
    short_name: `${name}`,
    name: `${name}`,
    icons: [
      {
        src: `${business?.logo || "/OneSiteCart/AppLogo192px.png"}?v=${Date.now()}`,
        sizes: "192x192",
        type: "image/png"
      },
      {
        src: `${business?.logo || "/OneSiteCart/AppLogo512px.png"}?v=${Date.now()}`,
        sizes: "512x512",
        type: "image/png"
      }
    ],
    start_url: "/",
    display: "standalone",
    theme_color: "#1f5eff",
    background_color: "#ffffff"
  };

  const blob = new Blob([JSON.stringify(manifest)], {
    type: "application/json"
  });

  const manifestURL = URL.createObjectURL(blob);

  const link = document.createElement("link");
  link.rel = "manifest";
  link.href = manifestURL;

  document.head.appendChild(link);
};


useEffect(() => {
  // if (!checkInit) return;
  const businessName = checkInit?.name?.primary?.trim() ? `${checkInit?.name?.primary} - Business Admin` : "Business Admin";

  document.title =  businessName;
  updateFavicon(checkInit?.logo || "/OneSiteCart/AppLogo192px.png");
  updateManifest(checkInit);
  updateAppleIcon(checkInit?.logo || "/OneSiteCart/AppLogo192px.png")

}, [checkInit]);

const updateAppleTitle = (name) => {
  let meta = document.querySelector(
    "meta[name='apple-mobile-web-app-title']"
  );

  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "apple-mobile-web-app-title");
    document.head.appendChild(meta);
  }

  meta.setAttribute("content", name);
};

const updateAppleIcon = (iconUrl) => {
  // remove old
  const oldIcons = document.querySelectorAll("link[rel='apple-touch-icon']");
  oldIcons.forEach(el => el.remove());

  const link = document.createElement("link");
  link.rel = "apple-touch-icon";

  link.href = `${iconUrl}?v=${Date.now()}`;

  document.head.appendChild(link);
};

const enableIOSWebApp = () => {
  const meta = document.createElement("meta");
  meta.name = "apple-mobile-web-app-capable";
  meta.content = "yes";
  document.head.appendChild(meta);
};

useEffect(() => {
  const businessName = checkInit?.name?.primary?.trim() ? `${checkInit?.name?.primary} - Business Admin` : "Business Admin";

  document.title =  businessName;

  updateAppleTitle(businessName);
  updateAppleIcon(checkInit?.logo || "/OneSiteCart/AppLogo192px.png");

  enableIOSWebApp();
}, [checkInit]);


useEffect(() =>{
getCheckInit();
},[])

  if (domainNotFound) return <NotFoundPage subText={subText} />;

  return (
      <ThemeProvider theme={lightTheme}>
        <GlobalStyles />
        <Routes />
        {orderAlert && (
          <StyledAlertModal
            open={true}
            maskClosable={false}
            closable={false}
            width={420}
             footer={[
            ]}
          >
            <CardWrapper>
              <CloseBtn
                onClick={() => {
                  stopSound();
                  setOrderAlert(null);
                }}
              >
                <CloseOutlined style={{ fontSize: 13 }} />
              </CloseBtn>

              <AlertBody
                onClick={() => {
                  stopSound();
                  setOrderAlert(null);
                  window.location.href = `/home/order-mgnt/orders/PENDING/order-details/${orderAlert?.orderId}`;
                }}
              >
                <IconBadge>
                  <InboxOutlined />
                </IconBadge>
                <div>
                  <AlertTitle>{t('order_alert_title')}</AlertTitle>
                  <AlertText>{orderAlert?.title}</AlertText>
                </div>
              </AlertBody>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    alignItems:"end",
                    height:"100%",
                    gap: 10,
                    marginTop: 28,
                    padding: "0 18px",
                  }}
                >
                  <CancelButton
                    onClick={() => {
                      stopSound();
                      setOrderAlert(null);
                    }}
                  >
                    {t("cancel")}
                  </CancelButton>

                  <GoButton
                    onClick={() => {
                      stopSound();
                      setOrderAlert(null);
                      window.location.href = `/home/order-mgnt/orders/PENDING/order-details/${orderAlert.orderId}`;
                    }}
                  >
                    {t("go-to-order")}
                  </GoButton>
                </div>
            </CardWrapper>
            
          </StyledAlertModal>
        )}
      </ThemeProvider>
  );
}

Boot().catch((err) => console.error(err));

export default App;